'use client'

import type { SlideInProps } from '@pearlconvert/ui/components/slide-in/slide-in.types'

import classNames from 'classnames'
import { FocusTrap } from 'focus-trap-react'
import { useTranslations } from 'next-intl'
import { CSSProperties, Suspense, useId } from 'react'

import { Button } from '@pearlconvert/ui/components/button'
import { Mask } from '@pearlconvert/ui/components/mask'
import { Preloader } from '@pearlconvert/ui/components/preloader'
import { useSlideIn } from '@pearlconvert/ui/components/slide-in/batons/use-slide-in'

import '@pearlconvert/ui/components/slide-in/styles/slide-in.scss'

export default function SlideIn({
  title,
  titleTag: TitleTag = 'h2',
  isOpen,
  animationSide = 'right',
  hasStickyHeader = true,
  customClassName = '',
  hasBackButton = false,
  hash,
  slideInType,
  onClose,
  onOpen,
  children,
  toastContainerRef,
  scrollToTop = true,
  modalId = 'modalId',
  openModals,
}: SlideInProps) {
  const t = useTranslations('ui')
  const titleId = useId()
  const {
    dialogRef,
    dialogContentRef,
    titleRef,
    hasBeenOpened,
    handleClose,
    handleBackButton,
    isScrolled,
    isTopmostModal,
    stackIndex,
  } = useSlideIn({
    title,
    hash,
    slideInType,
    onClose,
    isOpen,
    onOpen,
    toastContainerRef,
    scrollToTop,
    modalId,
    animationSide,
    openModals,
  })

  return (
    <>
      <FocusTrap
        active={isOpen as boolean}
        focusTrapOptions={{
          fallbackFocus: '.slidein',
          returnFocusOnDeactivate: false,
          escapeDeactivates: false,
          clickOutsideDeactivates: false,
          allowOutsideClick: true,
        }}
      >
        <dialog
          ref={dialogRef}
          className={classNames(
            'slidein',
            customClassName,
            `animation--${animationSide}`
          )}
          onClose={onClose}
          tabIndex={-1}
          data-slidein-id={modalId}
          {...(stackIndex && {
            style: { '--js-slidein-elevation': stackIndex } as CSSProperties,
          })}
          aria-label={typeof title === 'string' ? title : undefined}
          aria-labelledby={typeof title === 'string' ? undefined : titleId}
          aria-modal="true"
        >
          <div className="slidein__content" ref={dialogContentRef}>
            <div
              className={classNames('slidein__header', {
                'slidein__header--sticky': hasStickyHeader,
                'slidein__header--scrolled': isScrolled,
              })}
            >
              {hasBackButton && (
                <form method="dialog" className="slidein__back">
                  <Button
                    className="slidein__back-button"
                    type="empty"
                    buttonType="button"
                    iconLeft="arrow-left"
                    title={t('slide-in.back-to-prev')}
                    onClick={handleBackButton}
                  />
                </form>
              )}
              <TitleTag
                id={titleId}
                ref={titleRef}
                className="slidein__title"
                tabIndex={-1}
              >
                {title}
              </TitleTag>
              <form method="dialog" className="slidein__close">
                <Button
                  className="slidein__close-button"
                  type="empty"
                  iconLeft="close"
                  title={t('slide-in.close-panel', {
                    title: title?.toString() || '',
                  })}
                  onClick={handleClose}
                />
              </form>
            </div>
            {hasBeenOpened && (
              <Suspense fallback={<Preloader />}>{children}</Suspense>
            )}
          </div>
        </dialog>
      </FocusTrap>
      <Mask
        isActive={isTopmostModal && (isOpen as boolean)}
        elevation={stackIndex}
      />
    </>
  )
}
